import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Edit2 } from 'lucide-react';

const CustomDatePicker = ({ value, onChange, onClose }) => {
  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const parseDateString = (dateStr) => {
    if (!dateStr) return new Date();
    // Use a format that creates a local date: yyyy/mm/dd
    const [y, m, d] = dateStr.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
  };

  const [selectedDate, setSelectedDate] = useState(parseDateString(value));
  const [viewDate, setViewDate] = useState(new Date(selectedDate));
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const daysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleDateClick = (day) => {
    setSelectedDate(new Date(viewDate.getFullYear(), viewDate.getMonth(), day));
  };

  const renderDays = () => {
    const totalDays = daysInMonth(viewDate.getMonth(), viewDate.getFullYear());
    const startDay = firstDayOfMonth(viewDate.getMonth(), viewDate.getFullYear());
    const offset = (startDay + 1) % 7;

    const days = [];
    // Previous month days
    for (let i = 0; i < offset; i++) {
      days.push(<div key={`prev-${i}`} className="w-10 h-10"></div>);
    }

    for (let i = 1; i <= totalDays; i++) {
      const isSelected =
        selectedDate.getDate() === i &&
        selectedDate.getMonth() === viewDate.getMonth() &&
        selectedDate.getFullYear() === viewDate.getFullYear();

      days.push(
        <button
          key={i}
          type="button"
          onClick={() => handleDateClick(i)}
          className={`w-10 h-10 rounded-full flex items-center justify-center text-base font-medium transition-all ${
            isSelected
              ? 'bg-[#009944] text-white'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
          }`}
        >
          {i}
        </button>
      );
    }
    return days;
  };

  const formattedDate = `${selectedDate.getDate().toString().padStart(2, '0')} ${months[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-[28px] overflow-hidden shadow-2xl w-[380px]">
      {/* Green Header */}
      <div className="bg-[#009944] px-6 py-4 text-white">
        <p className="text-sm font-normal mb-2 opacity-90">Select date</p>
        <div className="flex justify-between items-center">
          <h2 className="text-[32px] font-normal leading-tight">{formattedDate}</h2>
          <Edit2 className="w-6 h-6" />
        </div>
      </div>

      {/* Calendar Body */}
      <div className="px-6 py-1 pb-4 bg-white dark:bg-gray-800">
        {/* Month/Year Navigation */}
        <div className="flex justify-between items-center mb-6">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMonthYearPicker(!showMonthYearPicker)}
              className="flex items-center gap-1 text-gray-700 dark:text-gray-300 text-base hover:bg-gray-100 dark:hover:bg-gray-700 px-2 py-1 rounded transition-colors"
            >
              {months[viewDate.getMonth()]} {viewDate.getFullYear()}
              <svg
                className={`w-4 h-4 transition-transform ${showMonthYearPicker ? 'rotate-180' : ''}`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>

            {/* Month/Year Dropdown */}
            {showMonthYearPicker && (
              <div className="absolute top-full left-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50 w-[320px]">
                {/* Year Selection */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() =>
                        setViewDate(new Date(viewDate.getFullYear() - 1, viewDate.getMonth(), 1))
                      }
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-base font-semibold text-gray-700 dark:text-gray-300">
                      {viewDate.getFullYear()}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setViewDate(new Date(viewDate.getFullYear() + 1, viewDate.getMonth(), 1))
                      }
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Month Grid */}
                <div className="grid grid-cols-3 gap-2">
                  {months.map((month, idx) => (
                    <button
                      key={month}
                      type="button"
                      onClick={() => {
                        setViewDate(new Date(viewDate.getFullYear(), idx, 1));
                        setShowMonthYearPicker(false);
                      }}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                        idx === viewDate.getMonth()
                          ? 'bg-[#009944] text-white'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {month.substring(0, 3)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              type="button"
              onClick={handleNextMonth}
              className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 mb-3">
          {['Sa', 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr'].map((day) => (
            <div key={day} className="text-center text-sm font-medium text-gray-500 w-10">
              {day}
            </div>
          ))}
        </div>

        {/* Days Grid */}
        <div className="grid grid-cols-7 gap-y-1">{renderDays()}</div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-8 pt-6 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[#009944] font-semibold text-base hover:opacity-70 transition-opacity"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onChange(formatDate(selectedDate));
              onClose();
            }}
            className="text-[#009944] font-semibold text-base hover:opacity-70 transition-opacity"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomDatePicker;
